import { getGeocode } from 'use-places-autocomplete';
import { useState } from 'react';
import { trackInsightsException } from '../../utils/telemetry/appinsights';
import { UserAddressDto } from '@kotipizzagroup/kotipizza-users-api-client';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { getDefaultRequestTimeout } from '../../utils/axiosUtils';

type Location = {
  coords: {
    latitude: number;
    longitude: number;
  };
};

const getAddressComponent = (
  result: google.maps.GeocoderResult,
  component: string
): string | null => {
  return (
    result.address_components.find(({ types }) => types.includes(component))?.long_name || null
  );
};

export const useCustomerLocation = (): {
  isLocationPermissionsMissing: boolean;
  locationError: boolean;
  getUserLocation(): Promise<Partial<UserAddressDto> | undefined>;
  getUserPosition(): Promise<google.maps.LatLngLiteral | undefined>;
  locationLoading: boolean;
} => {
  const [locationError, setLocationError] = useState(false);
  const [isLocationPermissionsMissing, setIsLocationPermissionsMissing] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  const getCurrentPositionWeb = (): Promise<Location> => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        timeout: getDefaultRequestTimeout(),
      });
    });
  };

  const getCurrentPositionCapacitor = async (): Promise<Location> => {
    try {
      const permissionState = await Geolocation.checkPermissions();

      // Check if permissions have been denied
      if (permissionState.location === 'denied' && permissionState.coarseLocation === 'denied') {
        setIsLocationPermissionsMissing(true);
      }
    } catch (e: unknown) {
      // Geolocation.checkPermissions() will throw when system location services have been
      // disabled, so catch with no-op here.
    }

    return Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: getDefaultRequestTimeout(),
    });
  };

  const getCurrentPosition = (): Promise<Location> => {
    if (!Capacitor.isNativePlatform()) {
      return getCurrentPositionWeb();
    }

    return getCurrentPositionCapacitor();
  };

  const getUserLocation = async (): Promise<Partial<UserAddressDto> | undefined> => {
    try {
      setLocationError(false);
      setIsLocationPermissionsMissing(false);
      setLocationLoading(true);

      const position = await getCurrentPosition();

      const { latitude, longitude } = position.coords;
      const geoCodeResult = await getGeocode({
        location: { lat: latitude, lng: longitude },
      });
      const streetAddressResult = geoCodeResult.filter((x) => {
        return x.types.includes('street_address') || x.types.includes('route');
      })[0];
      const city = getAddressComponent(streetAddressResult, 'locality') ?? '';
      const zipCode = getAddressComponent(streetAddressResult, 'postal_code') ?? '';
      const streetName = getAddressComponent(streetAddressResult, 'route') ?? '';
      const streetNumber = getAddressComponent(streetAddressResult, 'street_number') ?? '';
      setLocationLoading(false);
      return {
        streetAddress: `${streetName} ${streetNumber}`,
        city,
        zipCode,
        latitude: String(latitude),
        longitude: String(longitude),
      };
    } catch (err) {
      trackInsightsException('FRONTEND_USE_LOCATION_ERROR', err);
      setLocationLoading(false);
      setLocationError(true);
      return undefined;
    }
  };

  const getUserPosition = async (): Promise<google.maps.LatLngLiteral | undefined> => {
    try {
      setLocationError(false);
      setIsLocationPermissionsMissing(false);
      setLocationLoading(true);

      const position = await getCurrentPosition();
      const { latitude, longitude } = position.coords;
      setLocationLoading(false);
      return {
        lat: latitude,
        lng: longitude,
      };
    } catch (err) {
      trackInsightsException('FRONTEND_USE_LOCATION_ERROR', err);
      setLocationLoading(false);
      setLocationError(true);
      return undefined;
    }
  };

  return { isLocationPermissionsMissing, locationError, locationLoading, getUserLocation, getUserPosition };
};
